import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Image, Modal, Pagination, Row, Spinner, Table } from 'react-bootstrap';
import AdminSidebar from '../AdminComponent/AdminSidebar';
import Header from '../AdminComponent/Header';
import Footer from '../AdminComponent/Footer';
import { getManualPaymentOrders, updateManualPaymentOrder } from '../../../api/manualPaymentOrders';
import { formatNaira } from '../../../utils/money';

const paymentStatuses = ['pending_review', 'confirmed', 'rejected'];
const orderStatuses = ['pending', 'processing', 'completed', 'cancelled'];
const productSources = ['', 'main', 'oximo', 'aliexpress', 'aspl'];

const statusBadge = (value) => {
  if (value === 'confirmed' || value === 'completed') return 'success';
  if (value === 'rejected' || value === 'cancelled') return 'danger';
  if (value === 'processing') return 'primary';
  return 'warning';
};

const ManualPaymentOrdersPage = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState({ search: '', product_source: '', payment_status: '', status: '' });
  const [draft, setDraft] = useState({ payment_status: '', status: '', admin_note: '' });

  const loadOrders = async (targetPage = page) => {
    setLoading(true);
    setError('');
    try {
      const params = { page: targetPage, page_size: 10, ...filters };
      Object.keys(params).forEach((key) => params[key] === '' && delete params[key]);
      const res = await getManualPaymentOrders(params);
      setOrders(res.data.results || []);
      setPage(res.data.page || targetPage);
      setPages(res.data.pages || 1);
    } catch (err) {
      setError('Failed to load manual payment orders. Confirm you are logged in as an admin.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetails = (order) => {
    setSelectedOrder(order);
    setDraft({
      payment_status: order.payment_status || 'pending_review',
      status: order.status || 'pending',
      admin_note: order.admin_note || '',
    });
    setMessage('');
  };

  const handleFilterSubmit = (event) => {
    event.preventDefault();
    loadOrders(1);
  };

  const saveUpdate = async () => {
    if (!selectedOrder) return;
    setSaving(true);
    setMessage('');
    try {
      const res = await updateManualPaymentOrder(selectedOrder.id, draft);
      setSelectedOrder(res.data);
      setOrders((prev) => prev.map((order) => (order.id === res.data.id ? res.data : order)));
      setMessage('Manual payment order updated successfully.');
    } catch (err) {
      setMessage('Failed to update manual payment order.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="d-flex" style={{ minHeight: '100vh' }}>
      <AdminSidebar collapsed={collapsed} toggleSidebar={() => setCollapsed(!collapsed)} />
      <div style={{ marginLeft: collapsed ? '60px' : '200px', width: '100%', transition: 'margin-left 0.3s' }}>
        <Header />
        <Container fluid className="py-3">
          <Card className="shadow-sm border-0">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <h4 className="mb-0">Manual Payment Orders</h4>
              </div>

              <Form onSubmit={handleFilterSubmit} className="mb-3">
                <Row className="g-2">
                  <Col xs={12} lg={4}>
                    <Form.Control
                      placeholder="Search reference, customer, phone, email, product"
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    />
                  </Col>
                  <Col xs={12} md={4} lg={2}>
                    <Form.Select value={filters.product_source} onChange={(e) => setFilters({ ...filters, product_source: e.target.value })}>
                      {productSources.map((source) => <option key={source || 'all'} value={source}>{source || 'All sources'}</option>)}
                    </Form.Select>
                  </Col>
                  <Col xs={12} md={4} lg={2}>
                    <Form.Select value={filters.payment_status} onChange={(e) => setFilters({ ...filters, payment_status: e.target.value })}>
                      <option value="">All payment statuses</option>
                      {paymentStatuses.map((status) => <option key={status} value={status}>{status}</option>)}
                    </Form.Select>
                  </Col>
                  <Col xs={12} md={4} lg={2}>
                    <Form.Select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                      <option value="">All order statuses</option>
                      {orderStatuses.map((status) => <option key={status} value={status}>{status}</option>)}
                    </Form.Select>
                  </Col>
                  <Col xs={12} lg={2}>
                    <Button type="submit" className="w-100">Apply Filters</Button>
                  </Col>
                </Row>
              </Form>

              {error && <Alert variant="danger">{error}</Alert>}
              {loading ? (
                <div className="text-center py-5"><Spinner animation="border" /></div>
              ) : orders.length === 0 ? (
                <Alert variant="info">No manual payment orders found.</Alert>
              ) : (
                <Table responsive bordered hover className="align-middle">
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Customer</th>
                      <th>Phone</th>
                      <th>Source</th>
                      <th>Product</th>
                      <th>Total</th>
                      <th>Method</th>
                      <th>Payment</th>
                      <th>Order</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order.id}>
                        <td className="fw-semibold">{order.payment_reference}</td>
                        <td>{order.customer_name}</td>
                        <td>{order.customer_phone}</td>
                        <td><Badge bg="light" text="dark">{order.product_source}</Badge></td>
                        <td style={{ minWidth: 220 }}>{order.product_name}</td>
                        <td>{formatNaira(order.total_amount)}</td>
                        <td>{order.payment_method}</td>
                        <td><Badge bg={statusBadge(order.payment_status)}>{order.payment_status}</Badge></td>
                        <td><Badge bg={statusBadge(order.status)}>{order.status}</Badge></td>
                        <td>{order.created_at ? new Date(order.created_at).toLocaleString() : '-'}</td>
                        <td><Button size="sm" onClick={() => openDetails(order)}>View / Update</Button></td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}

              <Pagination>
                <Pagination.Prev disabled={page <= 1 || loading} onClick={() => loadOrders(page - 1)} />
                <Pagination.Item active>{page} / {pages}</Pagination.Item>
                <Pagination.Next disabled={page >= pages || loading} onClick={() => loadOrders(page + 1)} />
              </Pagination>
            </Card.Body>
          </Card>
        </Container>
        <Footer />
      </div>

      <Modal show={!!selectedOrder} onHide={() => setSelectedOrder(null)} size="xl" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Manual Payment Order Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedOrder && (
            <Row className="g-3">
              <Col xs={12} lg={7}>
                <Card className="shadow-sm border-0 h-100">
                  <Card.Body>
                    <h5>{selectedOrder.product_name}</h5>
                    {selectedOrder.product_image_url && <Image src={selectedOrder.product_image_url} thumbnail style={{ maxHeight: 180, objectFit: 'contain' }} />}
                    <Row className="mt-3 g-2">
                      <Col md={6}><strong>Reference:</strong> {selectedOrder.payment_reference}</Col>
                      <Col md={6}><strong>Source:</strong> {selectedOrder.product_source}</Col>
                      <Col md={6}><strong>Product ID:</strong> {selectedOrder.product_id}</Col>
                      <Col md={6}><strong>Unit Price:</strong> {formatNaira(selectedOrder.unit_price)}</Col>
                      <Col md={6}><strong>Quantity:</strong> {selectedOrder.quantity}</Col>
                      <Col md={6}><strong>Subtotal:</strong> {formatNaira(selectedOrder.subtotal)}</Col>
                      <Col md={6}><strong>Shipping:</strong> {selectedOrder.shipping_method || '-'}</Col>
                      <Col md={6}><strong>Shipping Fee:</strong> {formatNaira(selectedOrder.shipping_fee)}</Col>
                      <Col md={6}><strong>Shipping ETA:</strong> {selectedOrder.shipping_eta || '-'}</Col>
                      <Col md={6}><strong>Total:</strong> {formatNaira(selectedOrder.total_amount)}</Col>
                      <Col md={12}><strong>Product URL:</strong> <a href={selectedOrder.product_url} target="_blank" rel="noopener noreferrer">Open product</a></Col>
                    </Row>
                    <hr />
                    <h6>Customer</h6>
                    <p className="mb-1"><strong>Name:</strong> {selectedOrder.customer_name}</p>
                    <p className="mb-1"><strong>Phone:</strong> {selectedOrder.customer_phone}</p>
                    <p className="mb-1"><strong>Email:</strong> {selectedOrder.customer_email || '-'}</p>
                    <p className="mb-1"><strong>Note:</strong> {selectedOrder.customer_note || '-'}</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={12} lg={5}>
                <Card className="shadow-sm border-0 mb-3">
                  <Card.Body>
                    <h6>Proof of Payment</h6>
                    {selectedOrder.proof_url ? (
                      <>
                        <a href={selectedOrder.proof_url} target="_blank" rel="noopener noreferrer">Open proof image</a>
                        <div className="mt-2"><Image src={selectedOrder.proof_url} thumbnail style={{ maxHeight: 260, objectFit: 'contain' }} /></div>
                        <div className="small text-muted mt-2">Storage: {selectedOrder.proof_storage_provider}</div>
                      </>
                    ) : <Alert variant="warning">No proof URL saved.</Alert>}
                  </Card.Body>
                </Card>

                <Card className="shadow-sm border-0">
                  <Card.Body>
                    <h6>Admin Update</h6>
                    {message && <Alert variant={message.startsWith('Failed') ? 'danger' : 'success'}>{message}</Alert>}
                    <Form.Group className="mb-2">
                      <Form.Label>Payment Status</Form.Label>
                      <Form.Select value={draft.payment_status} onChange={(e) => setDraft({ ...draft, payment_status: e.target.value })}>
                        {paymentStatuses.map((status) => <option key={status} value={status}>{status}</option>)}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label>Order Status</Form.Label>
                      <Form.Select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })}>
                        {orderStatuses.map((status) => <option key={status} value={status}>{status}</option>)}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Admin Note</Form.Label>
                      <Form.Control as="textarea" rows={3} value={draft.admin_note} onChange={(e) => setDraft({ ...draft, admin_note: e.target.value })} />
                    </Form.Group>
                    <Button onClick={saveUpdate} disabled={saving}>{saving ? 'Saving...' : 'Save Update'}</Button>
                    <hr />
                    <p className="small mb-1"><strong>Reviewed At:</strong> {selectedOrder.reviewed_at ? new Date(selectedOrder.reviewed_at).toLocaleString() : '-'}</p>
                    <p className="small mb-1"><strong>Reviewed By:</strong> {selectedOrder.reviewed_by_name || selectedOrder.reviewed_by || '-'}</p>
                    <p className="small mb-1"><strong>Created:</strong> {selectedOrder.created_at ? new Date(selectedOrder.created_at).toLocaleString() : '-'}</p>
                    <p className="small mb-1"><strong>Updated:</strong> {selectedOrder.updated_at ? new Date(selectedOrder.updated_at).toLocaleString() : '-'}</p>
                    <details className="mt-2">
                      <summary>WhatsApp message</summary>
                      <pre className="small bg-light p-2 rounded mt-2" style={{ whiteSpace: 'pre-wrap' }}>{selectedOrder.whatsapp_message || '-'}</pre>
                    </details>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ManualPaymentOrdersPage;
